import React, { useState } from "react";
import {
  Box, Button, Card, Typography, Chip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, MenuItem, CircularProgress, Skeleton, TablePagination,
  Snackbar, Alert, InputAdornment, Tooltip, Avatar, Paper, Grid
} from "@mui/material";
import { Plus, Pencil, Trash2, X, FileText, Type, AlignLeft, Tag, Layers, Upload, FileCheck2, Search, User, Calendar } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import AdminShell from "../layout/AdminShell";
import dayjs from "dayjs";
import {
  adminGetResources, adminGetResourceById,
  adminCreateResource, adminUpdateResource, adminDeleteResource,
  adminGetResourceCategories,
} from "../../api/resources";
import { adminGetUsers } from "../../api/users";

// ── Status Constants ────────────────────────────────────────────────────────
const STATUSES = [
  { label: "Draft",     value: "DRAFT" },
  { label: "Published", value: "PUBLISHED" },
  { label: "Archived",  value: "ARCHIVED" },
];

const STATUS_STYLES = {
  PUBLISHED: { bg: "rgba(34,197,94,0.1)",  color: "#16A34A" },
  DRAFT:     { bg: "rgba(245,158,11,0.1)", color: "#B45309" },
  ARCHIVED:  { bg: "rgba(239,68,68,0.1)",  color: "#DC2626" },
};

const slugify = (str) =>
  str.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

// Tags helpers
const tagsToForm = (tags) => {
  if (!tags) return "";
  try {
    const parsed = JSON.parse(tags);
    return Array.isArray(parsed) ? parsed.join(", ") : String(tags);
  } catch {
    return String(tags);
  }
};

const tagsToApi = (str) => {
  if (!str || !str.trim()) return undefined;
  const arr = str.split(",").map(t => t.trim()).filter(Boolean);
  return JSON.stringify(arr);
};

const DEFAULT_VALUES = {
  title: "",
  slug: "",
  category: "",
  excerpt: "",
  body: "",
  publish_date: "",
  author: "",
  tags: "",
  seo_title: "",
  seo_description: "",
  status: "DRAFT",
  thumbnail: null,
};

export default function ResourcesAdminPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [thumbPreview, setThumbPreview] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { register, handleSubmit, reset, watch, control, formState: { errors } } = useForm({
    defaultValues: DEFAULT_VALUES,
  });

  const watchTitle = watch("title", "");
  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  // ── Queries ────────────────────────────────────────────────────────────────
  const { data: apiResources = [], isLoading } = useQuery({
    queryKey: ["admin-resources"],
    queryFn: () => adminGetResources().then(r => r.data),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["admin-resource-categories"],
    queryFn: () => adminGetResourceCategories().then(r => r.data),
  });

  const { data: users = [] } = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => adminGetUsers().then(r => r.data),
  });

  const resources = Array.isArray(apiResources) ? apiResources : [];
  const filteredResources = resources.filter(r =>
    r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.category_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.author_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const paginatedResources = filteredResources.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const totalCount = filteredResources.length;

  const createMutation = useMutation({
    mutationFn: (fd) => adminCreateResource(fd),
    onSuccess: () => {
      qc.invalidateQueries(["admin-resources"]);
      notify("Resource created");
      closeDialog();
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't create resource", "error"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminUpdateResource(id, data),
    onSuccess: () => {
      qc.invalidateQueries(["admin-resources"]);
      notify("Resource updated");
      closeDialog();
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't update resource", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: adminDeleteResource,
    onSuccess: () => {
      qc.invalidateQueries(["admin-resources"]);
      notify("Resource deleted");
      setConfirmDelete(null);
    },
    onError: (err) => {
      notify(err?.response?.data?.message || "Couldn't delete resource", "error");
      setConfirmDelete(null);
    },
  });

  const saving = createMutation.isPending || updateMutation.isPending;

  // ── Form Submit (CAREFULLY MATCHED TO API) ────────────────────────────────
  const onSubmit = (values) => {
    const fd = new FormData();

    // Required fields
    fd.append("title", values.title);
    fd.append("slug", values.slug?.trim() || slugify(values.title));

    // Optional text fields
    ["excerpt", "body", "seo_title", "seo_description", "status"].forEach(k => {
      if (values[k] !== undefined && values[k] !== "") fd.append(k, values[k]);
    });

    // Category (integer ID from select)
    if (values.category) fd.append("category", values.category);

    // Author (integer ID from select)
    if (values.author) fd.append("author", values.author);

    // Publish date (ISO string)
    if (values.publish_date) fd.append("publish_date", values.publish_date);

    // Tags (JSON array string)
    const tagsJson = tagsToApi(values.tags);
    if (tagsJson) fd.append("tags", tagsJson);

    // Thumbnail file (API field: thumbnail)
    if (values.thumbnail?.[0]) fd.append("thumbnail", values.thumbnail[0]);

    if (editing) updateMutation.mutate({ id: editing.id, data: fd });
    else createMutation.mutate(fd);
  };

  const openCreate = () => {
    setEditing(null);
    setThumbPreview(null);
    reset(DEFAULT_VALUES);
    setOpen(true);
  };

  const openEdit = async (r) => {
    setEditLoading(true);
    try {
      const { data } = await adminGetResourceById(r.id);
      setEditing(data);
      setThumbPreview(data.thumbnail_url || null);
      reset({
        title: data.title ?? "",
        slug: data.slug ?? "",
        category: data.category ?? "",
        excerpt: data.excerpt ?? "",
        body: data.body ?? "",
        publish_date: data.publish_date ? dayjs(data.publish_date).format("YYYY-MM-DDTHH:mm") : "",
        author: data.author ?? "",
        tags: tagsToForm(data.tags),
        seo_title: data.seo_title ?? "",
        seo_description: data.seo_description ?? "",
        status: data.status ?? "DRAFT",
      });
      setOpen(true);
    } catch (err) {
      notify(err?.response?.data?.message || "Couldn't load resource details", "error");
    } finally {
      setEditLoading(false);
    }
  };

  const closeDialog = () => {
    if (saving) return;
    setOpen(false);
    setEditing(null);
    setThumbPreview(null);
    reset(DEFAULT_VALUES);
  };

  const handleThumbChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setThumbPreview(URL.createObjectURL(file));
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  return (
    <AdminShell title="Resources">
      {/* Page header */}
      <Box sx={{
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        flexDirection: { xs: "column", sm: "row" },
        gap: 1.5, mb: 2.5,
      }}>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", maxWidth: 460 }}>
          Guides, blogs, webinars, and other resources published in the knowledge center.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={openCreate}
          sx={{
            borderRadius: "10px", textTransform: "none", fontWeight: 700,
            bgcolor: "#2563EB", boxShadow: "0 10px 24px -12px rgba(37,99,235,0.5)",
            flexShrink: 0, "&:hover": { bgcolor: "#1D4ED8" },
          }}
        >
          Add Resource
        </Button>
      </Box>

      {/* Search Bar */}
      <Card sx={{
        borderRadius: "16px",
        border: "1px solid rgba(15,23,42,0.08)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        p: 2, mb: 2.5,
      }}>
        <TextField
          fullWidth
          placeholder="Search resources by title, excerpt, category, or author..."
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} color="#94A3B8" />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              border: "1px solid rgba(15,23,42,0.15)",
              "&:hover": { borderColor: "#2563EB" },
            },
          }}
        />
      </Card>

      {/* Table Card */}
      <Card sx={{
        borderRadius: "16px",
        border: "1px solid rgba(15,23,42,0.08)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        overflow: "hidden"
      }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {["Resource", "Category", "Author", "Published", "Status", ""].map(h => (
                  <TableCell key={h} sx={{
                    fontWeight: 700, fontSize: 12, letterSpacing: "0.03em",
                    textTransform: "uppercase", color: "text.secondary",
                    borderBottom: "1px solid rgba(15,23,42,0.08)", whiteSpace: "nowrap"
                  }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" width={j === 0 ? 200 : 80} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedResources.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Box sx={{
                      width: 52, height: 52, borderRadius: "14px",
                      bgcolor: "rgba(37,99,235,0.07)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      mx: "auto", mb: 1.5
                    }}>
                      <FileText size={22} color="#2563EB" />
                    </Box>
                    <Typography fontWeight={700} color="#0F172A" mb={0.5}>
                      No resources found
                    </Typography>
                    <Typography color="text.secondary" fontSize={13.5}>
                      {searchQuery ? "Try adjusting your search" : "Add your first resource to publish it in the knowledge center."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedResources.map((r) => {
                const statusStyle = STATUS_STYLES[r.status] || STATUS_STYLES.DRAFT;
                return (
                  <TableRow key={r.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={{ minWidth: 260 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          variant="rounded"
                          src={r.thumbnail_url || undefined}
                          sx={{ width: 38, height: 38, borderRadius: "9px", bgcolor: "rgba(37,99,235,0.08)", color: "#2563EB", flexShrink: 0 }}
                        >
                          <FileCheck2 size={16} />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 13.75, color: "#0F172A", lineHeight: 1.3 }} noWrap>
                            {r.title}
                          </Typography>
                          {r.excerpt && (
                            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.2 }} noWrap>
                              {r.excerpt}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {r.category_name ? (
                        <Chip
                          label={r.category_name}
                          size="small"
                          sx={{ fontWeight: 600, fontSize: 11, height: 22, bgcolor: "rgba(37,99,235,0.07)", color: "#2563EB" }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>—</Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ fontSize: 13.5, color: "text.secondary", whiteSpace: "nowrap" }}>
                      {r.author_name || "—"}
                    </TableCell>

                    <TableCell sx={{ fontSize: 13, color: "text.secondary", whiteSpace: "nowrap" }}>
                      {r.publish_date ? dayjs(r.publish_date).format("DD MMM YYYY") : "—"}
                    </TableCell>

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Chip
                        label={r.status || "DRAFT"}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: 11, height: 22, bgcolor: statusStyle.bg, color: statusStyle.color }}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                        <Tooltip title="Edit">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openEdit(r)}
                              disabled={editLoading}
                              sx={{
                                border: "1px solid rgba(15,23,42,0.1)",
                                borderRadius: "8px",
                                "&:hover": { borderColor: "#2563EB", color: "#2563EB", bgcolor: "rgba(37,99,235,0.05)" }
                              }}
                            >
                              {editLoading ? <CircularProgress size={14} /> : <Pencil size={14} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => setConfirmDelete(r)}
                            sx={{
                              border: "1px solid rgba(15,23,42,0.1)",
                              borderRadius: "8px",
                              "&:hover": { borderColor: "#EF4444", color: "#EF4444", bgcolor: "rgba(239,68,68,0.05)" }
                            }}
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        {/* Pagination Controls */}
        {!isLoading && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid rgba(15,23,42,0.08)",
              "& .MuiTablePagination-toolbar": { padding: "16px 24px" },
              "& .MuiTablePagination-selectLabel": {
                fontSize: "13.5px", color: "text.secondary", marginRight: "8px",
              },
              "& .MuiTablePagination-displayedRows": { fontSize: "13.5px", color: "text.secondary" },
              "& .MuiInputBase-root": {
                borderRadius: "8px", border: "1px solid rgba(15,23,42,0.15)",
              },
              "& .MuiTablePagination-actions": { gap: "4px" },
              "& .MuiIconButton-root": {
                borderRadius: "8px", border: "1px solid rgba(15,23,42,0.1)", width: "32px", height: "32px",
              },
            }}
          />
        )}
      </Card>

      {/* Enhanced Create/Edit Dialog - BLUE GRADIENT FOR CONSISTENCY */}
      <Dialog
        open={open}
        onClose={closeDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            maxHeight: "90vh",
            overflow: "hidden",
            boxShadow: "0 30px 70px rgba(15,23,42,0.22)",
          }
        }}
      >
        <DialogTitle sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2.5,
          px: 3,
          background: "linear-gradient(135deg, #1D4ED8 0%, #2563EB 55%, #3B82F6 100%)",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.18)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <FileText size={22} color="#FFFFFF" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 21, color: "#FFFFFF", letterSpacing: "-0.01em" }}>
                {editing ? "Edit Resource" : "Add New Resource"}
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: "rgba(255,255,255,0.85)", fontWeight: 500, mt: 0.25 }}>
                {editing ? `Editing: ${editing.title}` : "Create a new resource for your knowledge center"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            pt: 3,
            pb: 1,
            px: 3,
            maxHeight: "65vh",
            overflowY: "auto",
            backgroundColor: "#F1F5F9",
          }}>
            {/* Content Section */}
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #2563EB",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "9px",
                  backgroundColor: "rgba(37,99,235,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Type size={17} color="#2563EB" />
                </Box>
                <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A" }}>
                  Content
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Title"
                    fullWidth
                    disabled={saving}
                    required
                    error={!!errors.title}
                    helperText={errors.title ? "Title is required" : ""}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Type size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("title", { required: true })}
                  />
                </Box>

                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Slug"
                    fullWidth
                    disabled={saving}
                    placeholder={watchTitle ? slugify(watchTitle) : "auto-generated from title"}
                    helperText="Leave blank to auto-generate. Lowercase letters, numbers, hyphens only."
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Search size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("slug")}
                  />
                </Box>

                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Excerpt"
                    fullWidth
                    multiline
                    rows={2}
                    disabled={saving}
                    placeholder="Short summary shown in listings"
                    InputProps={{
                      startAdornment: <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1.5 }}>
                        <AlignLeft size={16} color="#94A3B8" />
                      </InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("excerpt")}
                  />
                </Box>

                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Body"
                    fullWidth
                    multiline
                    rows={6}
                    disabled={saving}
                    placeholder="Full content of the resource"
                    InputLabelProps={{ shrink: true }}
                    {...register("body")}
                  />
                </Box>
              </Box>
            </Paper>

            {/* Metadata Section */}
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #7C3AED",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "9px",
                  backgroundColor: "rgba(124,58,237,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Layers size={17} color="#7C3AED" />
                </Box>
                <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A" }}>
                  Metadata
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <Controller
                    name="category"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        select
                        label="Category"
                        fullWidth
                        disabled={saving}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><Layers size={16} color="#94A3B8" /></InputAdornment>
                        }}
                        InputLabelProps={{ shrink: true }}
                        {...field}
                        value={field.value ?? ""}
                      >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {categories.map(c => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Box>

                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <Controller
                    name="author"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        select
                        label="Author"
                        fullWidth
                        disabled={saving}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><User size={16} color="#94A3B8" /></InputAdornment>
                        }}
                        InputLabelProps={{ shrink: true }}
                        {...field}
                        value={field.value ?? ""}
                      >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {users.filter(u => u.is_active).map(u => (
                          <MenuItem key={u.id} value={u.id}>
                            <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
                              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0F172A" }}>
                                {u.full_name || u.email}
                              </Typography>
                              {u.full_name && (
                                <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                                  {u.email}
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Box>

                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <TextField
                    label="Publish Date"
                    type="datetime-local"
                    fullWidth
                    disabled={saving}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Calendar size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("publish_date")}
                  />
                </Box>

                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <TextField
                    label="Tags"
                    fullWidth
                    disabled={saving}
                    placeholder="cloud, aws, devops"
                    helperText="Comma-separated. Sent to the API as a JSON array."
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Tag size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("tags")}
                  />
                </Box>
              </Box>
            </Paper>

            {/* SEO Section */}
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #EA580C",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "9px",
                  backgroundColor: "rgba(234,88,12,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Search size={17} color="#EA580C" />
                </Box>
                <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A" }}>
                  SEO Settings
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="SEO Title"
                    fullWidth
                    disabled={saving}
                    placeholder="Overrides page title for search engines"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Type size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("seo_title")}
                  />
                </Box>

                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="SEO Description"
                    fullWidth
                    multiline
                    rows={3}
                    disabled={saving}
                    placeholder="Meta description for search engines"
                    InputProps={{
                      startAdornment: <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1.5 }}>
                        <AlignLeft size={16} color="#94A3B8" />
                      </InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("seo_description")}
                  />
                </Box>
              </Box>
            </Paper>

            {/* Status & Thumbnail Section */}
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #2563EB",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "9px",
                  backgroundColor: "rgba(37,99,235,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Tag size={17} color="#2563EB" />
                </Box>
                <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A" }}>
                  Publish Settings
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        select
                        label="Status"
                        fullWidth
                        disabled={saving}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><Tag size={16} color="#94A3B8" /></InputAdornment>
                        }}
                        InputLabelProps={{ shrink: true }}
                        {...field}
                        value={field.value ?? ""}
                      >
                        {STATUSES.map(s => (
                          <MenuItem key={s.value} value={s.value}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Box sx={{
                                width: 8, height: 8, borderRadius: "50%",
                                bgcolor: STATUS_STYLES[s.value]?.bg || "rgba(15,23,42,0.08)"
                              }} />
                              {s.label}
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Box>

                <Box sx={{ gridColumn: "span 12" }}>
                  <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    borderRadius: "12px",
                    border: "2px dashed rgba(37,99,235,0.2)",
                    backgroundColor: "rgba(37,99,235,0.03)",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: "#2563EB",
                      backgroundColor: "rgba(37,99,235,0.05)"
                    }
                  }}>
                    <Avatar
                      variant="rounded"
                      src={thumbPreview || undefined}
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "12px",
                        bgcolor: "rgba(37,99,235,0.08)",
                        color: "#2563EB",
                        flexShrink: 0
                      }}
                    >
                      <FileText size={24} />
                    </Avatar>
                    <Box>
                      <Button
                        component="label"
                        variant="contained"
                        size="small"
                        disabled={saving}
                        startIcon={<Upload size={14} />}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: "8px",
                          bgcolor: "#2563EB",
                          "&:hover": { bgcolor: "#1D4ED8" }
                        }}
                      >
                        {thumbPreview ? "Change Image" : "Upload Image"}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          {...register("thumbnail")}
                          onChange={(e) => {
                            register("thumbnail").onChange(e);
                            handleThumbChange(e);
                          }}
                        />
                      </Button>
                      <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                        Recommended: 800x450px, JPG/PNG
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </DialogContent>

          <DialogActions sx={{
            px: 3, py: 2.25,
            borderTop: "1px solid rgba(15,23,42,0.08)",
            backgroundColor: "#FFFFFF",
            display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5,
          }}>
            <Button
              onClick={closeDialog}
              disabled={saving}
              sx={{
                textTransform: "none", fontWeight: 700, color: "#475569",
                px: 2.5, py: 1, borderRadius: "10px",
                backgroundColor: "rgba(15,23,42,0.04)",
                "&:hover": { backgroundColor: "rgba(15,23,42,0.08)" },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                textTransform: "none", fontWeight: 700, borderRadius: "10px",
                background: "linear-gradient(135deg, #1D4ED8 0%, #2563EB 100%)",
                px: 3.5, py: 1.1, minWidth: 140,
                boxShadow: "0 4px 14px rgba(37,99,235,0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #1E40AF 0%, #1D4ED8 100%)",
                  boxShadow: "0 6px 18px rgba(37,99,235,0.4)"
                },
              }}
            >
              {saving ? "Saving..." : editing ? "Update Resource" : "Create Resource"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => !deleteMutation.isPending && setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px" } }}
      >
        <DialogTitle sx={{
          pb: 1,
          borderBottom: "1px solid rgba(15,23,42,0.08)",
        }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Delete this resource?</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ fontSize: 13.75, color: "text.secondary" }}>
            "{confirmDelete?.title}" will be removed from the knowledge center immediately. This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          px: 3, pb: 2.5,
          borderTop: "1px solid rgba(15,23,42,0.08)",
        }}>
          <Button
            onClick={() => setConfirmDelete(null)}
            disabled={deleteMutation.isPending}
            sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => deleteMutation.mutate(confirmDelete.id)}
            disabled={deleteMutation.isPending}
            variant="contained"
            startIcon={deleteMutation.isPending ? <CircularProgress size={15} color="inherit" /> : null}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: "10px",
              bgcolor: "#EF4444", px: 2.5, minWidth: 100,
              "&:hover": { bgcolor: "#DC2626" }
            }}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13.5 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminShell>
  );
}